// Service targets (PLAN §13.3) and the performance report (task 47). The load run on staging
// (`e2e/perf`, `.github/workflows/perf.yml`) measures each target, and this module decides pass or miss and writes
// the report. The year-1 scale test in the app Worker (apps/app/test/performance.test.ts) guards the queries.

/** How a target's samples are summarized. */
export type TargetStat = 'p75' | 'p95' | 'max';

export type ServiceTargetKey =
  | 'page_load'
  | 'search'
  | 'graph_expand'
  | 'chat_first_token'
  | 'sheet_ready'
  | 'transcript_indexed'
  | 'sample_set_ingested'
  | 'availability'
  | 'backup';

export interface ServiceTarget {
  key: ServiceTargetKey;
  /** As PLAN §13.3 names it. */
  label: string;
  /** As PLAN §13.3 writes it. */
  target: string;
  /** Milliseconds at `stat`; null for targets the load run doesn't time (availability, backups). */
  limitMs: number | null;
  stat: TargetStat;
  /** What the load run times, in words (also the help article's table). */
  measure: string;
}

const MIN = 60_000;

export const SERVICE_TARGETS: readonly ServiceTarget[] = [
  {
    key: 'page_load',
    label: 'Page load (p75, desktop)',
    target: '< 2 s',
    limitMs: 2_000,
    stat: 'p75',
    measure:
      'Opening Home, the Directory, a profile, the graph, the Library and Reports in a desktop browser until the page heading shows.',
  },
  {
    key: 'search',
    label: 'Search / autocomplete',
    target: '< 300 ms p95',
    limitMs: 300,
    stat: 'p95',
    measure:
      'Global search (⌘K: its text half, which answers first; D388) and the entity picker, answered by the server, for names from the real sample set.',
  },
  {
    key: 'graph_expand',
    label: 'Graph expand (≤ 500 nodes)',
    target: '< 1 s',
    limitMs: 1_000,
    stat: 'max',
    measure: 'Expanding the most linked entities, and re-reading the links among up to 500 entities on screen.',
  },
  {
    key: 'chat_first_token',
    label: 'Chat first token',
    target: '< 3 s',
    limitMs: 3_000,
    stat: 'p95',
    measure: 'From sending a question to the first streamed event of the answer (the chat shows what it is doing from then on).',
  },
  {
    key: 'sheet_ready',
    label: 'Profile sheet (DOCX) → ready for review',
    target: '< 2 min',
    limitMs: 2 * MIN,
    stat: 'max',
    measure: 'Each Actor Profile Sheet of the real sample set, from the end of its upload to Waiting for review.',
  },
  {
    key: 'transcript_indexed',
    label: '1-hour transcript import → mentions indexed',
    target: '< 5 min',
    limitMs: 5 * MIN,
    stat: 'max',
    measure: 'Each interview transcript of the real sample set, from the end of its upload to indexed and extracted.',
  },
  {
    key: 'sample_set_ingested',
    label: 'Full sample set (73 files) → all indexed and extracted',
    target: '< 2 h',
    limitMs: 120 * MIN,
    stat: 'max',
    measure: 'The whole real sample set uploaded at once, until every file is indexed and extracted.',
  },
  {
    key: 'availability',
    label: 'Availability (monthly)',
    target: '99.5%',
    limitMs: null,
    stat: 'max',
    measure: 'Not timed by the load run: the 5-minute synthetic checks (Admin → Monitoring) track it.',
  },
  {
    key: 'backup',
    label: 'Backup RPO / RTO',
    target: '24 h / 4 h',
    limitMs: null,
    stat: 'max',
    measure: 'Not timed by the load run: the nightly backup and the restore drill (Admin → Backups) cover it.',
  },
];

export const serviceTarget = (key: ServiceTargetKey): ServiceTarget => SERVICE_TARGETS.find(t => t.key === key) as ServiceTarget;

/** Nearest-rank percentile (p in 0–100) of the samples; NaN when there are none. */
export function percentile(samples: readonly number[], p: number): number {
  const xs = samples.filter(Number.isFinite).sort((a, b) => a - b);
  if (!xs.length) return Number.NaN;
  const rank = Math.ceil((Math.min(Math.max(p, 0), 100) / 100) * xs.length);
  return xs[Math.max(rank, 1) - 1] as number;
}

export function statOf(samples: readonly number[], stat: TargetStat): number {
  if (stat === 'max') return samples.length ? Math.max(...samples) : Number.NaN;
  return percentile(samples, stat === 'p75' ? 75 : 95);
}

export type TargetOutcome = 'met' | 'missed' | 'not_measured';

export interface TargetResult {
  key: ServiceTargetKey;
  label: string;
  target: string;
  stat: TargetStat;
  /** Samples taken. */
  n: number;
  /** Value at `stat`, in ms; null when not measured. */
  valueMs: number | null;
  outcome: TargetOutcome;
  /** Why it wasn't measured, or what the samples were. */
  note?: string;
}

/** Summarizes one target's samples (ms). No samples, or a target the run doesn't time, is `not_measured`. */
export function evaluateTarget(key: ServiceTargetKey, samples: readonly number[], note?: string): TargetResult {
  const t = serviceTarget(key);
  const base = { key, label: t.label, target: t.target, stat: t.stat, n: samples.length, ...(note ? { note } : {}) };
  if (t.limitMs === null || !samples.length) return { ...base, valueMs: null, outcome: 'not_measured' };
  const valueMs = Math.round(statOf(samples, t.stat));
  return { ...base, valueMs, outcome: valueMs < t.limitMs ? 'met' : 'missed' };
}

export interface PerfReport {
  environment: string;
  commit: string | null;
  startedAt: number;
  finishedAt: number;
  /** The real sample set as sent: files, unique by content, and where every document ended up. */
  dataset: { files: number; unique: number; uploaded: number; alreadyStored: number; statuses: Record<string, number> };
  results: TargetResult[];
  notes: string[];
}

/** Every timed target was met (not-measured ones don't fail the run; missed ones do). */
export const reportPassed = (r: Pick<PerfReport, 'results'>) => r.results.every(x => x.outcome !== 'missed');

/** "850 ms", "1.4 s", "3 min 20 s". */
export function formatTargetDuration(ms: number): string {
  if (ms < 1_000) return `${Math.round(ms)} ms`;
  if (ms < MIN) return `${(Math.round(ms / 100) / 10).toLocaleString('en')} s`;
  const m = Math.floor(ms / MIN);
  const s = Math.round((ms % MIN) / 1_000);
  return s ? `${m} min ${s} s` : `${m} min`;
}

const OUTCOME_LABEL: Record<TargetOutcome, string> = { met: 'Met', missed: 'Missed', not_measured: 'Not measured' };

/** The report as Markdown (the run's job summary and the artifact). */
export function perfReportMarkdown(r: PerfReport): string {
  const when = new Date(r.startedAt).toISOString().replace('T', ' ').slice(0, 16);
  const lines = [
    `# Performance run on ${r.environment} — ${reportPassed(r) ? 'all timed targets met' : 'targets missed'}`,
    '',
    `${when} UTC · ${formatTargetDuration(r.finishedAt - r.startedAt)}${r.commit ? ` · commit ${r.commit.slice(0, 7)}` : ''}`,
    '',
    `Real sample set: ${r.dataset.files} files, ${r.dataset.unique} unique; ${r.dataset.uploaded} uploaded in this run, ${r.dataset.alreadyStored} already stored.`,
    `Where they ended: ${
      Object.entries(r.dataset.statuses)
        .sort((a, b) => b[1] - a[1])
        .map(([s, n]) => `${s} ${n}`)
        .join(', ') || 'none'
    }.`,
    '',
    '| Measure | Target | Result | Samples | Outcome |',
    '|---|---|---|---|---|',
    ...r.results.map(
      x =>
        `| ${x.label} | ${x.target} | ${x.valueMs === null ? '—' : `${formatTargetDuration(x.valueMs)} (${x.stat})`} | ${x.n} | ${OUTCOME_LABEL[x.outcome]}${x.note ? ` — ${x.note}` : ''} |`,
    ),
  ];
  if (r.notes.length) lines.push('', ...r.notes.map(n => `- ${n}`));
  return `${lines.join('\n')}\n`;
}
